import moment from 'moment-timezone';
import { useEffect, useState, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Customer, RootState, TripArrangeStop, TripWithStopsDetails, Vehicle } from '../../store/config/types';
import { fetchCouriers } from '../../store/actions/couriers.actions';
import { couriersService } from '../../services/couriers.service';
import tripsService from '../../services/trips.service';
import { useHistory } from 'react-router-dom';
import { fetchTrips } from '../../store/actions/trips.actions';
import { DeliveryServiceProvider } from '../../store/config/types/deliveryServiceProvider.types';
import { Warehouse } from '../../store/config/types/warehouses.types';
import TripEditView from './TripEditView';
import { TripOptimizingMethod } from '../../store/config/enums/trips.enum';
import { VehiclesService } from '../../services/vehicles.service';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTargetDate } from '../../store/context/date.context';

// import TripRow from '../../components/TripsDnd/TripRow';

// interface EditTripsParams {
//   date: string;
// }

const mapStateToProps = (state: RootState) => {
  return {
    couriers: state.couriers,
    trips: state.trips,
    loggedIn: state.auth.loggedIn,
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, any>) => ({
  fetchCouriers: () => dispatch(fetchCouriers()),
  fetchTrips: (from: Date | null = null, to: Date | null = null) => dispatch(fetchTrips(from, to)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

function TripEdit({ fetchCouriers, fetchTrips, couriers, loggedIn, auth }: PropsFromRedux) {
  if (auth.account?.shipper?.timezone) moment.tz.setDefault(auth.account?.shipper?.timezone);

  const { date } = useTargetDate();
  const history = useHistory();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [availableCouriers, setAvailableCouriers] = useState<number[]>([]);
  const [deliveryServiceProviders, setDeliveryServiceProviders] = useState<DeliveryServiceProvider[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // const [searchText, setSearchText] = useState<string>('');
  const [arrangedTrips, setArrangedTrips] = useState<TripWithStopsDetails[] | undefined>(undefined);
  const [unAssignStops, setUnAssignStops] = useState<TripArrangeStop[]>([]);
  const [defaultOptimizingMethod, setDefaultOptimizingMethod] = useState<TripOptimizingMethod>(
    TripOptimizingMethod.NONE,
  );

  const fetchDeliveryServiceProviders = () => {
    setDeliveryServiceProviders([]);
    if (auth?.account?.shipper?.shipperId) {
      couriersService.fetchDeliveryServiceProviders().then((dsps) => setDeliveryServiceProviders(dsps ?? []));
    }
  };

  const handleCancel = () => {
    tripsService
      .cancelEditTrip()
      .then((result) => {
        if (result) history.goBack();
      })
      .finally(() => {
        // setSaving(false);
      });
  };

  const afterSave = () => {
    fetchTrips(moment(date, 'YYYY-MM-DD').toDate(), moment(date, 'YYYY-MM-DD').toDate()).finally(() => {
      history.goBack();
    });
  };

  useEffect(() => {
    fetchDeliveryServiceProviders();
    if (!vehicles.length) {
      VehiclesService.fetchVehicles([], true).then((fetchedVehicles) => {
        setVehicles(fetchedVehicles ?? []);
      });
    }
  }, []);

  useEffect(() => {
    if (!couriers.loadingCouriers && !couriers.couriers && !couriers.couriersErrorMessage) {
      fetchCouriers();
    }
  }, [fetchCouriers, couriers.couriers, couriers.couriersErrorMessage, couriers.loadingCouriers, loggedIn]);

  useEffect(() => {
    if (date) {
      tripsService
        .fetchTripsOfDateForEdit({ date: moment(date).toDate() })
        .then((result) => {
          if (result?.defaultTripOptimizingMethod) setDefaultOptimizingMethod(result.defaultTripOptimizingMethod);
          if (result?.customers) setCustomers(result.customers);
          if (result?.warehouses) setWarehouses(result.warehouses);
          const trips = result?.trips;
          if (trips) {
            const unAssignedStops = trips.filter((trip) => trip.tripId === -1);
            setUnAssignStops(unAssignedStops.length > 0 ? unAssignedStops[0].tripStops : []);
            setArrangedTrips(JSON.parse(JSON.stringify(trips?.filter((trip) => trip.tripId !== -1))));
          }
        })
        .catch((e) => console.error(e));

      couriersService.fetchCouriersByDate(date).then((couriers) => {
        if (couriers) {
          setAvailableCouriers(couriers.map((c) => c.courierId ?? 0));
        }
      });
    }
  }, [date]);

  // --- Session refresh and inactivity logic ---
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [showInactivityPrompt]);

  // Handle session refresh and inactivity
  useEffect(() => {
    // Clear previous timers
    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);

    const checkInterval = setInterval(() => {
      const now = Date.now();
      const msSinceLastActivity = now - (lastActivityRef?.current ?? 0);

      // If user was active in last 5 minutes, refresh session
      console.log('last activity', msSinceLastActivity / 1000);

      if (msSinceLastActivity < 5 * 60 * 1000) {
        tripsService.sendEditSessionRefreshSignal();
      }

      // If user inactive for 5+ minutes, show prompt
      if (msSinceLastActivity >= 5 * 60 * 1000 && msSinceLastActivity < 10 * 60 * 1000) {
        setShowInactivityPrompt(true);
      }

      // If user inactive for 10+ minutes, auto-cancel
      if (msSinceLastActivity >= 10 * 60 * 1000) {
        handleCancel();
      }
    }, 60000);

    return () => clearInterval(checkInterval);
  }, [date]); // re-run if date changes

  // When user responds to inactivity prompt
  const handleInactivityPromptResponse = () => {
    setShowInactivityPrompt(false);
    lastActivityRef.current = Date.now();
    tripsService.sendEditSessionRefreshSignal();
  };

  return (
    <>
      <TripEditView
        attributedDate={moment(date, 'YYYY-MM-DD').toDate()}
        availableCourierIds={availableCouriers}
        couriers={couriers?.couriers ?? []}
        vehicles={vehicles}
        customers={customers}
        date={date}
        deliveryServiceProviders={deliveryServiceProviders}
        timezone={auth?.account?.timezone}
        trips={arrangedTrips ?? []}
        unassigned={unAssignStops ?? []}
        warehouses={warehouses}
        onCancel={handleCancel}
        onAfterSave={afterSave}
        defaultOptimizingMethod={defaultOptimizingMethod}
      />
      {/* Inactivity prompt */}

      <Dialog open={showInactivityPrompt} onClose={() => handleInactivityPromptResponse()}>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <p>
            Your editing session will expire soon due to inactivity.
            <br />
            Click below to continue editing.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInactivityPromptResponse} variant="contained">
            Continue Editing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default connector(TripEdit);
